import MediaPlayer, {MediaPlayerConfig} from "./MediaPlayer";
import m, {Vnode, VnodeDOM} from "mithril";
import _ from "underscore";
import PlayPause from "./controls/PlayPause";
import Replay from "./controls/Replay";
import Volume from "./controls/Volume";
import Seeker from "./controls/Seeker";
import CurrentTime from "./controls/CurrentTime";
import Duration from "./controls/Duration";

export type AudioPlayerConfig = MediaPlayerConfig & {
    type: 'audio/mpeg'|'audio/ogg'|'audio/wav'|'audio/webm'|string,
};

export default class AudioPlayer extends MediaPlayer {

    constructor(
        link: string,
        config: AudioPlayerConfig,
        onPassMinElapsedTime?: (()=>void),
    ) {
        super(link, config, onPassMinElapsedTime)

        this.config.type = config.type || 'audio/mpeg';
        //this.config.mini = true;
    }

    public oncreate(vnode: VnodeDOM) {
        this._self.mediaElement = <HTMLAudioElement>$(vnode.dom).find('audio')[0];
        m.redraw();

        if (window.navigator.onLine) {
            // safari workaround (oncanplay does not work)
            setTimeout(() => {
                if (this.loading) {
                    this.loading = false;
                    if (this.config.autoplay) {
                        this.play();
                    }
                    m.redraw();
                }
            }, 2000)
        } else {
            this._self.error = "No internet connection.";
        }

    }


    // View
    public view(): Vnode {
        return m('.AudioPlayer.'+this.config.skin+(this.error?'.hasError':''), {}, [
            this.error ? m('.error', {
                onclick: () => {
                    this.reload();
                },
            }, m.trust('Loading failed. Click to reload media.<br />'+this.error)) : null,
            this.viewAudio(),
            this.viewLink(),
            this.loading ? this.viewLoading() : this.viewPlayer(),
        ]);
    }

    private viewLoading(): Vnode {
        return m('.loading', {}, [
            m('.icon.icon-cached.spinning',{title: "Loading"}),
        ]);
    }

    private viewAudio(): Vnode {
        return m('audio', {
            ontimeupdate: () => this.onTimeUpdate(),
            onended: () => this.onEnded(),
            oncanplay: () => {
                this.loading = false;
                this.error = null;
                if (this.config.autoplay) {
                    this.play();
                }
                m.redraw();
            },
            onwaiting: () => this.waiting = true,
            onerror: () => this.error = "Error: "+this.link,
            onstalled: () => this.error = "Error: "+this.link,
            onplaying: () => this.waiting = false,
            autoplay: this.config.autoplay,
            onloadeddata: () => {
                this.loading = false;
                this.waiting = false;
                this.error = null;
            }
        }, [
            m('source', {
                src: this.link,
                type: this.config.type,
            }),
        ]);
    }

    private viewPlayer(): Vnode {
        switch (this.config.skin) {
            case "mini": return m('.player', {}, [
                this.viewArtwork(),
                m('.player-controls', {}, [
                    m(this.controls.playPause),
                    m(this.controls.replay),
                    m(this.controls.seeker),
                    m(this.controls.volume)
                ]),
            ]);
            case "simple": return m('.player', {}, [
                this.viewArtwork(),
                m('.player-controls', {}, [
                    m(this.controls.playPause),
                    m(this.controls.replay),
                ]),
            ]);
        }

        return m('.player', {}, [
            this.viewArtwork(),
            m('.timing', {}, [
                m(this.controls.currentTime),
                m(this.controls.duration),
            ]),
            m(this.controls.seeker),
            this.viewControls(),
        ])

    }

    private viewLink(): Vnode {
        return m('.link', {}, [
            m('a', {
                href: this.link,
                target: '_blank',
            }, this.link.split('/').pop()?.split('#')[0].split('?')[0] || this.link)
        ]);
    }

    private viewArtwork(): Vnode {
        return m('.artwork' + (this.isPlaying()?'.playing':'') + (this.isWaiting()?'.waiting':''), {
            onclick: () => this.playPause(),
        }, [
            this.isWaiting() ? m('i.icon.icon-cached.spinning') : m('i.icon.icon-music-simple'),
        ])
    }

    private viewControls(): Vnode {
        return m('.player-controls', {}, [
            m(this.controls.replay),
            m(this.controls.playPause),
            m(this.controls.volume),
        ]);
    }

}